import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Users, CheckCircle, Clock, AlertCircle, ArrowRight, Activity,
  Plus, Calendar, Ticket, UserPlus, MapPin, MessageCircle, KeyRound, Cpu,
  Sparkles,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────
function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Gute Nacht";
  if (h < 12) return "Guten Morgen";
  if (h < 18) return "Guten Tag";
  return "Guten Abend";
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border px-3 py-2 text-xs shadow-soft">
      <p className="font-bold mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span style={{ color: p.color }}>■</span>
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const STATUS_LABELS = { open: "Offen", in_progress: "In Bearbeitung", done: "Erledigt" };
const PRIORITY_LABELS = { high: "Hoch", medium: "Mittel", low: "Niedrig" };

const ACTIVITY_ICONS = {
  customer: Users, customer_created: UserPlus, customer_updated: Users,
  ticket: Ticket, ticket_created: Ticket, ticket_updated: Ticket,
  password: KeyRound, hardware: Cpu,
  event_created: Calendar, event_updated: Calendar,
  login: Activity, comment: MessageCircle,
};

const CATEGORY_COLORS = {
  onsite: "#2563eb", remote: "#0ea5e9", maintenance: "#f59e0b",
  meeting: "#8b5cf6", vacation: "#10b981", other: "#64748b",
};

// ── Component ────────────────────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ stats: null, today_events: [], top_customers: [], recent_activity: [] });
  const [tickets, setTickets] = useState([]);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    api.get("/dashboard/overview").then(r => setData(r.data)).catch(() => {});
    api.get("/dashboard/recent-tickets").then(r => setTickets(r.data)).catch(() => {});
    api.get("/dashboard/activity-stats").then(r => setChartData(r.data)).catch(() => {});
  }, []);

  const stats = data.stats || {};
  const today = new Date();
  const dateLabel = today.toLocaleDateString("de-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* ── Hero / Greeting ───────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            {greeting()}, {user?.name?.split(" ")[0] || "Team"} <span className="inline-block animate-wave">👋</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1 capitalize">{dateLabel}</p>
        </div>
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          <button data-testid="qa-new-customer" onClick={() => navigate("/customers")}
            className="flex items-center gap-1.5 px-3 py-2 bg-card border border-border hover:border-primary text-sm font-semibold transition-colors">
            <UserPlus size={14} className="text-primary" /> Kunde
          </button>
          <button data-testid="qa-new-ticket" onClick={() => navigate("/tickets")}
            className="flex items-center gap-1.5 px-3 py-2 bg-card border border-border hover:border-primary text-sm font-semibold transition-colors">
            <Ticket size={14} className="text-primary" /> Ticket
          </button>
          <button data-testid="qa-new-event" onClick={() => navigate("/calendar")}
            className="flex items-center gap-1.5 px-3 py-2 bg-card border border-border hover:border-primary text-sm font-semibold transition-colors">
            <Calendar size={14} className="text-primary" /> Termin
          </button>
        </div>
      </div>

      {/* ── KPI Cards (clickable) ─────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Kunden gesamt" value={stats.total_customers ?? "—"}
          sub={`${stats.active_customers ?? 0} aktiv`}
          icon={Users} accent="primary"
          onClick={() => navigate("/customers")}
          testId="stat-kunden-gesamt"
        />
        <KpiCard
          label="Offene Tickets" value={stats.open_tickets ?? "—"}
          sub="Nicht zugewiesen oder in Wartung"
          icon={AlertCircle} accent="warning"
          onClick={() => navigate("/tickets")}
          testId="stat-offene-tickets"
        />
        <KpiCard
          label="In Bearbeitung" value={stats.in_progress_tickets ?? "—"}
          sub="Aktuell aktiv"
          icon={Clock} accent="info"
          onClick={() => navigate("/tickets")}
          testId="stat-in-bearbeitung"
        />
        <KpiCard
          label="Erledigt" value={stats.done_tickets ?? "—"}
          sub="Insgesamt abgeschlossen"
          icon={CheckCircle} accent="success"
          onClick={() => navigate("/tickets")}
          testId="stat-erledigt"
        />
      </div>

      {/* ── Today's events + Top customers (2-col) ────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Today */}
        <div className="bg-card border border-border shadow-soft" data-testid="today-events-card">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Sparkles size={13} className="text-primary" /> Heute
            </h2>
            <Link to="/calendar" className="text-xs text-primary flex items-center gap-1 hover:underline">
              Kalender <ArrowRight size={11} />
            </Link>
          </div>
          {data.today_events.length === 0 ? (
            <div className="p-6 text-center">
              <Calendar size={28} className="mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">Keine Termine heute</p>
              <button onClick={() => navigate("/calendar")} className="mt-3 text-xs text-primary hover:underline flex items-center gap-1 mx-auto">
                <Plus size={11} /> Termin anlegen
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {data.today_events.slice(0, 6).map(ev => (
                <button
                  key={ev._id}
                  onClick={() => navigate("/calendar")}
                  className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-muted/40 text-left transition-colors"
                  data-testid={`today-event-${ev._id}`}
                >
                  <span className="w-1 self-stretch flex-shrink-0 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[ev.category] || "#64748b" }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{ev.title}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      <Clock size={10} />
                      {ev.all_day ? "Ganztägig" : new Date(ev.start).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}
                      {ev.location && <><MapPin size={10} className="ml-1" />{ev.location}</>}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Top customers */}
        <div className="bg-card border border-border shadow-soft lg:col-span-2" data-testid="top-customers-card">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <AlertCircle size={13} className="text-primary" /> Kunden mit offenen Tickets
            </h2>
            <Link to="/tickets" className="text-xs text-primary flex items-center gap-1 hover:underline">
              Alle Tickets <ArrowRight size={11} />
            </Link>
          </div>
          {data.top_customers.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle size={32} className="mx-auto mb-2 text-emerald-500" />
              <p className="text-sm font-semibold">Alles erledigt 🎉</p>
              <p className="text-xs text-muted-foreground mt-1">Keine offenen Tickets bei Kunden</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {data.top_customers.map(c => (
                <button
                  key={c.customer_id}
                  onClick={() => navigate(`/customers/${c.customer_id}`)}
                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-muted/40 text-left transition-colors group"
                  data-testid={`top-customer-${c.customer_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="initials-avatar w-9 h-9 text-sm flex-shrink-0">
                      {c.name.split(" ").map(p => p[0]).filter(Boolean).slice(0, 2).join("")}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.open_tickets} offene Tickets</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${Math.min(100, c.open_tickets * 20)}%` }} />
                    </div>
                    <span className="text-xs font-mono font-bold w-6 text-right">{c.open_tickets}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Activity Chart ──────────────────────────────────── */}
      <div className="bg-card border border-border shadow-soft" data-testid="activity-chart">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
          <Activity size={14} className="text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
            Aktivitäten letzte 14 Tage
          </h2>
        </div>
        <div className="px-4 py-4" style={{ height: 260 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="colorActivities" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(18, 72%, 56%)" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="hsl(18, 72%, 56%)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorKunden" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(158, 60%, 42%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(158, 60%, 42%)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(38, 90%, 55%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(38, 90%, 55%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval={1} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  formatter={(v) => v === "activities" ? "Aktivitäten" : v === "neue_kunden" ? "Neue Kunden" : "Neue Tickets"} />
                <Area type="monotone" dataKey="activities" name="activities"
                  stroke="hsl(18, 72%, 56%)" fill="url(#colorActivities)" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="neue_kunden" name="neue_kunden"
                  stroke="hsl(158, 60%, 42%)" fill="url(#colorKunden)" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="neue_tickets" name="neue_tickets"
                  stroke="hsl(38, 90%, 55%)" fill="url(#colorTickets)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              Noch keine Daten vorhanden
            </div>
          )}
        </div>
      </div>

      {/* ── Recent tickets + activity (2-col) ────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent tickets */}
        <div className="bg-card border border-border shadow-soft lg:col-span-2">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
              Aktuelle Tickets
            </h2>
            <Link to="/tickets" className="text-xs text-primary flex items-center gap-1 hover:underline">
              Alle <ArrowRight size={11} />
            </Link>
          </div>
          {tickets.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle size={28} className="mx-auto mb-2 text-emerald-500" />
              <p className="text-sm text-muted-foreground">Keine offenen Tickets</p>
            </div>
          ) : (
            <div className="divide-y divide-border" data-testid="recent-tickets-list">
              {tickets.slice(0, 6).map(t => (
                <button key={t._id}
                  onClick={() => navigate(`/customers/${t.customer_id}`)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-muted/40 text-left transition-colors group">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{t.title}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{t.customer_name}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[10px] font-bold priority-${t.priority}`}>
                      {PRIORITY_LABELS[t.priority]?.toUpperCase()}
                    </span>
                    <span className={`text-[10px] font-bold ${{ open: "status-open", in_progress: "status-in-progress", done: "status-done" }[t.status]}`}>
                      {STATUS_LABELS[t.status]?.toUpperCase()}
                    </span>
                    {t.assigned_initials && (
                      <span className="initials-avatar w-7 h-7 text-xs" title={t.assigned_name}>{t.assigned_initials}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent activity feed */}
        <div className="bg-card border border-border shadow-soft" data-testid="recent-activity-card">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Activity size={13} className="text-primary" /> Letzte Aktivität
            </h2>
            <Link to="/security" className="text-xs text-primary flex items-center gap-1 hover:underline">
              Alle <ArrowRight size={11} />
            </Link>
          </div>
          {data.recent_activity.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Noch keine Aktivität</div>
          ) : (
            <div className="divide-y divide-border max-h-[340px] overflow-y-auto">
              {data.recent_activity.map(a => {
                const Icon = ACTIVITY_ICONS[a.action] || ACTIVITY_ICONS[a.entity_type] || Activity;
                const detailText = typeof a.details === "string"
                  ? a.details
                  : a.details?.title || a.details?.name || "";
                return (
                  <div key={a._id} className="flex items-start gap-2.5 px-4 py-2.5 text-xs">
                    <span className="initials-avatar w-6 h-6 text-[10px] flex-shrink-0 mt-0.5">{a.user_initials}</span>
                    <div className="min-w-0 flex-1">
                      <p className="leading-snug">
                        <span className="font-semibold">{a.user_name}</span>
                        {detailText && <span className="text-muted-foreground"> · {detailText}</span>}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Icon size={9} />
                        {new Date(a.timestamp).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── KPI Card Component ───────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, accent, onClick, testId }) {
  const accentClasses = {
    primary: "bg-primary/10 text-primary",
    warning: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    info: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
    success: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="bg-card border border-border shadow-soft p-5 text-left hover:border-primary hover:shadow-md transition-all group animate-in"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
          <p className="text-3xl font-bold mt-1 tracking-tight group-hover:text-primary transition-colors" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {value}
          </p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-md flex-shrink-0 ${accentClasses[accent] || accentClasses.primary}`}>
          <Icon size={18} />
        </div>
      </div>
    </button>
  );
}
